import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
	Stack,
	Pagination,
	TextField,
	InputAdornment,
	Button,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Page from '../../components/Page';
import CodeIcon from '@mui/icons-material/Code';
import { get, toWapi } from '../../util/api';

class InstanceLogout extends Component {
	state = {
		logs: [],
		groupedLogs: [],
		phoneStatusMap: {},
		loading: true,
		error: null,
		currentPage: 1,
		itemsPerPage: 50,
		searchTerm: '',
		detailOpen: false,
		detailPhone: null,
		detailLoading: false,
		detailLogs: [],
		startDate: new Date().toISOString().split('T')[0],
		endDate: new Date().toISOString().split('T')[0],
	};

	componentDidMount() {
		this.fetchData();
	}

	fetchData = async () => {
		this.setState({ loading: true, error: null });

		try {
			const [logs, phoneStatusMap] = await Promise.all([this.fetchLogs(), this.fetchPhones()]);
			const groupedLogs = this.groupByPhone(logs);

			this.setState({
				logs,
				groupedLogs,
				phoneStatusMap,
				loading: false,
			});
		} catch (error) {
			console.error('Error fetching data:', error);
			this.setState({
				loading: false,
				error: error.message || 'An error occurred while fetching data',
			});
		}
	};

	fetchLogData = async () => {
		this.setState({ loading: true, error: null });

		try {
			const logs = await this.fetchLogs();
			const groupedLogs = this.groupByPhone(logs);

			this.setState({
				logs,
				groupedLogs,
				loading: false,
			});
		} catch (error) {
			console.error('Error fetching data:', error);
			this.setState({
				loading: false,
				error: error.message || 'An error occurred while fetching data',
			});
		}
	};

	fetchLogs = async () => {
		const { product } = this.props;
		const pid = product.id;
		const uid = product.uid;
		const { startDate, endDate } = this.state;

		const response = await get(`/maytapi/logoutLogs?pid=${pid}&uid=${uid}&startDate=${startDate}&endDate=${endDate}`);
		const result = response.data || response;

		if (result.success && result.data) {
			return result.data;
		} else if (Array.isArray(result)) {
			return result;
		}
		return [];
	};

	fetchPhones = async () => {
		const { product } = this.props;
		const path = '/api/' + product.id + '/listPhones';
		const response = await toWapi(path, product.settings.wapi_token);

		const statusMap = {};
		if (response.data && Array.isArray(response.data)) {
			response.data.forEach(function (phone) {
				statusMap[String(phone.id)] = phone.status || 'unknown';
			});
		}
		return statusMap;
	};

	groupByPhone = (logs) => {
		const map = {};

		logs.forEach(function (log) {
			const phone = log.extra && log.extra.phone !== undefined ? String(log.extra.phone) : 'N/A';
			if (!map[phone]) {
				map[phone] = {
					phone: phone,
					latestDate: log.date,
					count: 1,
				};
			} else {
				map[phone].count += 1;
				if (new Date(log.date) > new Date(map[phone].latestDate)) {
					map[phone].latestDate = log.date;
				}
			}
		});

		const grouped = Object.keys(map).map(function (key) {
			return map[key];
		});

		grouped.sort(function (a, b) {
			return new Date(b.latestDate) - new Date(a.latestDate);
		});

		return grouped;
	};

	handlePageChange = (event, value) => {
		this.setState({ currentPage: value });
	};

	handleSearchChange = (event) => {
		this.setState({ searchTerm: event.target.value, currentPage: 1 });
	};

	handleDetailOpen = async (phone) => {
		this.setState({ detailOpen: true, detailPhone: phone, detailLogs: [], detailLoading: true });

		try {
			const { product } = this.props;
			const pid = product.id;
			const uid = product.uid;

			const endDate = new Date().toISOString().split('T')[0];
			const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

			const response = await get(`/maytapi/logoutLogs?pid=${pid}&uid=${uid}&startDate=${startDate}&endDate=${endDate}&phone=${phone}`);
			const result = response.data || response;
			const logs = Array.isArray(result) ? result : (result.success && result.data) ? result.data : [];

			const sorted = logs.sort(function (a, b) {
				return new Date(b.date) - new Date(a.date);
			});

			this.setState({ detailLogs: sorted, detailLoading: false });
		} catch (error) {
			this.setState({ detailLoading: false });
		}
	};

	handleDetailClose = () => {
		this.setState({
			detailOpen: false,
			detailPhone: null,
			detailLogs: [],
		});
	};

	getStatus = (phone) => {
		if (phone === 'N/A') return 'N/A';
		return this.state.phoneStatusMap[phone] || 'unknown';
	};

	formatDate = (dateStr) => {
		if (!dateStr) return 'N/A';
		try {
			return new Date(dateStr).toLocaleString('tr-TR');
		} catch (e) {
			return dateStr;
		}
	};

	downloadCSV = () => {
		const filteredData = this.filterData(this.state.groupedLogs);
		if (!filteredData.length) return;

		const self = this;
		const headers = ['Phone', 'Current Status', 'Logout Count', 'Last Logout Date'];
		const csvData = filteredData.map(function (item) {
			return [item.phone, self.getStatus(item.phone), item.count, item.latestDate];
		});

		const csvContent = [headers]
			.concat(csvData)
			.map(function (row) {
				return row
					.map(function (field) {
						return '"' + String(field).replace(/"/g, '""') + '"';
					})
					.join(',');
			})
			.join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', 'instance-logout-' + new Date().toISOString().split('T')[0] + '.csv');
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	filterData = (data) => {
		const { searchTerm, selectedDate } = this.state;
		let filtered = data;

		if (selectedDate) {
			filtered = filtered.filter(function (item) {
				return item.latestDate && item.latestDate.startsWith(selectedDate);
			});
		}

		if (searchTerm) {
			const lower = searchTerm.toLowerCase();
			filtered = filtered.filter(function (item) {
				return item.phone.includes(lower);
			});
		}

		return filtered;
	};

	handleStartDateChange = (event) => {
		this.setState({ startDate: event.target.value, currentPage: 1 });
	};

	handleEndDateChange = (event) => {
		this.setState({ endDate: event.target.value, currentPage: 1 });
	};

	render() {
		const { loading, error, currentPage, itemsPerPage, searchTerm, groupedLogs, detailOpen, detailPhone, detailLogs } = this.state;

		if (loading) {
			return (
				<Page title="Developers" subtitle="Instance Logout Logs" icon={<CodeIcon />}>
					<Stack sx={{ marginTop: '20%', marginLeft: '50%' }}>
						<CircularProgress disableShrink />
					</Stack>
				</Page>
			);
		}

		if (error) {
			return (
				<Page title="Developers" subtitle="Instance Logout Logs" icon={<CodeIcon />}>
					<Typography variant="h6" color="error" sx={{ textAlign: 'center', mt: 4 }}>
						{error}
					</Typography>
					<Typography variant="body1" sx={{ textAlign: 'center', mt: 1 }}>
						Please try refreshing the page or check your connection.
					</Typography>
				</Page>
			);
		}

		const filteredData = this.filterData(groupedLogs);
		const indexOfLastItem = currentPage * itemsPerPage;
		const indexOfFirstItem = indexOfLastItem - itemsPerPage;
		const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
		const pageCount = Math.ceil(filteredData.length / itemsPerPage);

		return (
			<Page title="Developers" subtitle="Instance Logout Logs" icon={<CodeIcon />}>
				<Stack spacing={2}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Typography variant="body2" color="textSecondary">
							Lists instance logout events.
						</Typography>
						<Button variant="contained" startIcon={<DownloadIcon />} onClick={this.downloadCSV} disabled={!filteredData.length}>
							Download CSV
						</Button>
					</Box>

					<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
						<TextField
							sx={{ width: 500 }}
							variant="outlined"
							placeholder="Search by Phone"
							value={searchTerm}
							onChange={this.handleSearchChange}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
						<TextField
							type="date"
							variant="outlined"
							label="Start Date"
							value={this.state.startDate}
							onChange={this.handleStartDateChange}
							InputLabelProps={{ shrink: true }}
							onKeyDown={(e) => e.preventDefault()}
							inputProps={{ max: new Date().toISOString().split('T')[0] }}
						/>
						<TextField
							type="date"
							variant="outlined"
							label="End Date"
							value={this.state.endDate}
							onChange={this.handleEndDateChange}
							InputLabelProps={{ shrink: true }}
							onKeyDown={(e) => e.preventDefault()}
							inputProps={{ max: new Date().toISOString().split('T')[0] }}
						/>
						<Button
							variant="contained"
							onClick={() => this.fetchLogData()}
							sx={{ height: '53px', width: '75px', whiteSpace: 'nowrap' }}
							disabled={this.state.startDate > this.state.endDate}
						>
							Filter
						</Button>
					</Box>

					<TableContainer component={Paper}>
						<Table sx={{ background: '#fafafa' }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ width: '15%' }}>
										<strong>Phone</strong>
									</TableCell>
									<TableCell sx={{ width: '15%' }}>
										<strong>Current Status</strong>
									</TableCell>
									<TableCell sx={{ width: '15%' }}>
										<strong>Logout Count</strong>
									</TableCell>
									<TableCell sx={{ width: '35%' }}>
										<strong>Last Logout Date</strong>
									</TableCell>
									<TableCell sx={{ width: '20%' }}>
										<strong>Details</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!currentItems.length ? (
									<TableRow>
										<TableCell style={{ textAlign: 'center' }} colSpan={5}>
											No matching results found.
										</TableCell>
									</TableRow>
								) : (
									currentItems.map((item, index) => (
										<TableRow key={item.phone || index}>
											<TableCell>{item.phone}</TableCell>
											<TableCell>{this.getStatus(item.phone)}</TableCell>
											<TableCell>{item.count}</TableCell>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{this.formatDate(item.latestDate)}</TableCell>
											<TableCell>
												<Button
													size="small"
													variant="outlined"
													startIcon={<VisibilityIcon />}
													onClick={() => this.handleDetailOpen(item.phone)}
												>
													View
												</Button>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{pageCount > 1 && (
						<Stack spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
							<Pagination count={pageCount} page={currentPage} onChange={this.handlePageChange} color="primary" showFirstButton showLastButton />
						</Stack>
					)}

					<Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
						Total Records: {filteredData.length}
					</Typography>
				</Stack>

				<Dialog open={detailOpen} onClose={this.handleDetailClose} maxWidth="sm" fullWidth>
					<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						{'Phone ' + (detailPhone || '') + ' - Logout History'}
						<IconButton onClick={this.handleDetailClose} size="small">
							<CloseIcon />
						</IconButton>
					</DialogTitle>
					<DialogContent dividers>
						{this.state.detailLoading ? (
							<Stack alignItems="center" sx={{ py: 4 }}>
								<CircularProgress disableShrink />
							</Stack>
						) : (
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												<strong>#</strong>
											</TableCell>
											<TableCell>
												<strong>Logout Date</strong>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{detailLogs.map((log, index) => (
											<TableRow key={log.id || index}>
												<TableCell>{detailLogs.length - index}</TableCell>
												<TableCell>{this.formatDate(log.date)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleDetailClose}>Close</Button>
					</DialogActions>
				</Dialog>
			</Page>
		);
	}
}

export default connect(function (state) {
	return {
		user: state.access.user.loggedUserObj,
		product: state.product.maytapi,
	};
})(InstanceLogout);
